import { ValidationResult } from '../types';

/**
 * Validation utilities for CV Builder forms
 */

/**
 * Validate required string field
 */
export const validateRequired = (value: string, fieldName: string): string[] => {
  const errors: string[] = [];
  if (!value || value.trim().length === 0) {
    errors.push(`${fieldName} wajib diisi`);
  }
  return errors;
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): string[] => {
  const errors: string[] = [];
  if (!email) return errors;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    errors.push('Masukkan alamat email yang valid');
  }
  return errors;
};

/**
 * Validate URL format (optional field)
 */
export const validateURL = (url: string, fieldName: string): string[] => {
  const errors: string[] = [];
  if (!url) return errors; // Optional field
  
  try {
    new URL(url);
  } catch {
    errors.push(`Masukkan URL yang valid untuk ${fieldName}`);
  }
  return errors;
};

/**
 * Validate phone number format
 */
export const validatePhoneNumber = (phone: string): string[] => {
  const errors: string[] = [];
  if (!phone) return errors;
  
  // Basic phone validation - adjust regex based on requirements
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  if (!phoneRegex.test(phone.replace(/[\s\-()]/g, ''))) {
    errors.push('Masukkan nomor telepon yang valid');
  }
  return errors;
};

/**
 * Validate year range (end year should be after start year)
 */
export const validateYearRange = (startYear: number, endYear?: number): string[] => {
  const errors: string[] = [];
  const currentYear = new Date().getFullYear();
  
  if (startYear > currentYear) {
    errors.push('Tahun mulai tidak boleh di masa depan');
  }
  
  if (endYear && endYear < startYear) {
    errors.push('Tahun selesai harus setelah tahun mulai');
  }
  
  if (endYear && endYear > currentYear) {
    errors.push('Tahun selesai tidak boleh di masa depan');
  }
  
  return errors;
};

/**
 * Validate GPA format (optional, should be between 0-4 or 0-100)
 */
export const validateGPA = (gpa: string): string[] => {
  const errors: string[] = [];
  if (!gpa) return errors; // Optional field
  
  const gpaNum = parseFloat(gpa);
  if (isNaN(gpaNum)) {
    errors.push('IPK harus berupa angka yang valid');
  } else if (gpaNum < 0 || gpaNum > 100) {
    errors.push('IPK harus antara 0 dan 100');
  }
  
  return errors;
};

/**
 * Validate YouTube URL specifically
 */
export const validateYouTubeURL = (url: string): string[] => {
  const errors: string[] = [];
  if (!url) return errors; // Optional field
  
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/;
  if (!youtubeRegex.test(url)) {
    errors.push('Masukkan URL YouTube yang valid');
  }
  
  return errors;
};

/**
 * Combine multiple validation results
 */
export const combineValidationResults = (...results: ValidationResult[]): ValidationResult => {
  const combinedErrors: Record<string, string[]> = {};
  let isValid = true;
  
  results.forEach(result => {
    if (!result.isValid) {
      isValid = false;
    }
    
    Object.entries(result.errors).forEach(([field, fieldErrors]) => {
      if (!combinedErrors[field]) {
        combinedErrors[field] = [];
      }
      combinedErrors[field].push(...fieldErrors);
    });
  });
  
  return {
    isValid,
    errors: combinedErrors
  };
};

/**
 * Validate CV identity form data
 */
export const validateCVIdentityData = (data: any): ValidationResult => {
  const errors: Record<string, string[]> = {};
  
  // Required fields validation
  errors.full_name = validateRequired(data.full_name, 'Nama lengkap');
  errors.birth_place = validateRequired(data.birth_place, 'Tempat lahir');
  errors.birth_date = validateRequired(data.birth_date, 'Tanggal lahir');
  errors.address = validateRequired(data.address, 'Alamat');
  errors.phone_number = validateRequired(data.phone_number, 'Nomor telepon');
  errors.email = validateRequired(data.email, 'Email');
  errors.dream_job = validateRequired(data.dream_job, 'Pekerjaan impian');
  errors.main_interest = validateRequired(data.main_interest, 'Minat utama');
  errors.interest_reason = validateRequired(data.interest_reason, 'Alasan minat');
  errors.profile_summary = validateRequired(data.profile_summary, 'Ringkasan profil');
  
  // Format validation
  if (data.email) {
    errors.email.push(...validateEmail(data.email));
  }
  
  if (data.phone_number) {
    errors.phone_number.push(...validatePhoneNumber(data.phone_number));
  }
  
  // Optional URL validation
  if (data.linkedin_url) {
    errors.linkedin_url = validateURL(data.linkedin_url, 'URL LinkedIn');
  }
  
  if (data.portfolio_url) {
    errors.portfolio_url = validateURL(data.portfolio_url, 'URL Portofolio');
  }
  
  // Remove empty error arrays
  Object.keys(errors).forEach(key => {
    if (errors[key].length === 0) {
      delete errors[key];
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};